// ─────────────────────────────────────────────────────────────────────────────
// Shared application submission for the membership application pages
// (venue / vendor / boutique). Mirrors the already-fixed flow on
// ApplyForInclusion: listing_applications (blocking) + leads (with lead_channel)
// + autoresponder. Keeps one source of truth for new application pages.
// ─────────────────────────────────────────────────────────────────────────────
import { supabase } from '../lib/supabaseClient.js';

// Applicant confirmation email - on-brand, no em dashes (house style).
function buildEmail({ name, businessName }) {
  const first = (name || '').trim().split(/\s+/)[0] || 'there';
  const html = `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#0b0a08;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#0b0a08;padding:48px 16px;"><tr><td align="center">
  <table width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;background:#111009;border-radius:4px;border-top:3px solid #d39a21;">
    <tr><td style="padding:44px 48px 28px;text-align:center;border-bottom:1px solid rgba(255,255,255,0.06);">
      <span style="font-family:Georgia,serif;font-size:13px;letter-spacing:3px;text-transform:uppercase;color:#d39a21;">&#10022; &nbsp; L W D &nbsp; &#10022;</span>
    </td></tr>
    <tr><td style="padding:40px 48px 8px;">
      <p style="font-family:Georgia,serif;font-size:22px;font-style:italic;color:#f2efea;margin:0 0 24px;">Thank you, ${first}.</p>
      <p style="font-size:15px;color:rgba(242,239,234,0.7);line-height:1.8;margin:0 0 18px;">We have received your application to join the Collection${businessName ? ` on behalf of <strong style="color:#f2efea;">${businessName}</strong>` : ''}.</p>
      <p style="font-size:15px;color:rgba(242,239,234,0.7);line-height:1.8;margin:0 0 18px;">Every application is reviewed personally by our editorial team. We accept a limited number of partners, and we will be in touch within 3 business days.</p>
    </td></tr>
    <tr><td style="background:#0b0a08;padding:24px 48px;text-align:center;">
      <p style="font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:rgba(242,239,234,0.35);margin:0;">Luxury Wedding Directory &middot; 5 Star Weddings Ltd</p>
    </td></tr>
  </table></td></tr></table></body></html>`;
  return { subject: 'We have received your application to the Collection', html };
}

export async function submitApplication({ form, sourcePage, leadSource, leadType = 'membership_application', category }) {
  // Step 1 - listing_applications is the only blocking step.
  const { error: appErr } = await supabase.from('listing_applications').insert([{
    name:          form.name,
    email:         form.email,
    phone:         form.phone || null,
    business_name: form.businessName,
    category,
    country:       form.country || null,
    region:        form.region || null,
    website:       form.website || null,
    instagram:     form.instagram || null,
    message:       form.message || null,
    source_page:   sourcePage,
    status:        'new',
  }]);
  if (appErr) return { ok: false, error: appErr };

  // Steps 2 + 3 - fire-and-forget; never block the success state.
  const parts = (form.name || '').trim().split(/\s+/);
  supabase.from('leads').insert([{
    first_name:  parts[0] || form.name,
    last_name:   parts.slice(1).join(' ') || '',
    email:       form.email,
    phone:       form.phone || null,
    lead_source: leadSource,
    lead_channel: 'form',
    lead_type:   leadType,
    status:      'new',
    requirements_json: {
      businessName: form.businessName, category,
      country: form.country, region: form.region,
      website: form.website, instagram: form.instagram, message: form.message,
    },
  }]).then(({ error }) => { if (error) console.warn('[application] lead create:', error.message); });

  const tpl = buildEmail({ name: form.name, businessName: form.businessName });
  supabase.functions.invoke('send-email', { body: { ...tpl, to: form.email } })
    .catch((e) => console.warn('[application] autoresponder:', e?.message));

  return { ok: true };
}
